import React, { useState, useEffect, useRef } from 'react';
import { optimizeUnsplashImage, generateSrcSet, getImageSizes } from '../utils/imageOptimization';
import { useSiteContent } from '../context/SiteContentContext';

const OptimizedImage = ({
  src,
  alt,
  type = 'default',
  className = '',
  width,
  height,
  loading = 'lazy',
  priority = false,
  onLoad,
  imageKey, // New prop for CMS override
  ...props
}) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [error, setError] = useState(false);
  const imgRef = useRef(null);

  // Check CMS for overrides
  const { getImage } = useSiteContent();

  // Determine final source: CMS override (if key provided) -> prop src
  const finalSrc = imageKey ? getImage(imageKey, src) : src;

  // Optimize the main image source
  const optimizedSrc = optimizeUnsplashImage(finalSrc, {
    width: width || 800,
    height,
    quality: 80
  });

  // Generate responsive srcset
  const srcSet = generateSrcSet(finalSrc);

  // Get appropriate sizes attribute
  const sizes = getImageSizes(type);

  useEffect(() => {
    if (priority && imgRef.current) {
      // Preload priority images
      const link = document.createElement('link');
      link.rel = 'preload';
      link.as = 'image';
      link.href = optimizedSrc;
      document.head.appendChild(link);

      return () => {
        document.head.removeChild(link);
      };
    }
  }, [priority, optimizedSrc]);

  const handleLoad = () => {
    setIsLoaded(true);
    if (onLoad) onLoad();
  };

  const handleError = () => {
    setError(true);
  };

  return (
    <div className={`relative overflow-hidden ${className}`}>
      {/* Placeholder/blur effect while loading */}
      {!isLoaded && !error && (
        <div
          className="absolute inset-0 bg-slate-200 dark:bg-slate-800 animate-pulse"
          aria-hidden="true"
        />
      )}

      {/* Actual image */}
      {!error && (
        <img
          ref={imgRef}
          src={optimizedSrc}
          srcSet={srcSet}
          sizes={sizes}
          alt={alt}
          loading={priority ? 'eager' : loading}
          onLoad={handleLoad}
          onError={handleError}
          className={`w-full h-full object-cover transition-opacity duration-300 ${isLoaded ? 'opacity-100' : 'opacity-0'
            }`}
          {...props}
        />
      )}

      {/* Error fallback */}
      {error && (
        <div className="absolute inset-0 flex items-center justify-center bg-slate-100 dark:bg-slate-800">
          <p className="text-slate-500 dark:text-slate-400 text-sm">
            Failed to load image
          </p>
        </div>
      )}
    </div>
  );
};

export default OptimizedImage;